import React, { FunctionComponent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useIsFocused } from 'record-it-components/hooks/useIsFocused';
import { SearchInput } from 'record-it-components/input/SearchInput/SearchInput';
import { PageContentContainer } from 'record-it-components/pageContentLayout/PageContentContainer/PageContentContainer';
import { PageContentHeader } from 'record-it-components/pageContentLayout/PageContentHeader/PageContentHeader';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { CreateEntityButton } from 'record-it-components/misc/CreateEntityButton/CreateEntityButton';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { useRequiredWorkTimeEntryToCreate } from '../../../hooks/createWorkTimeEntry/useRequiredWorkTimeEntryToCreate';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { useConfigureBreadcrumbsWithBuilder } from '../../../hooks/misc/useConfigureBreadcrumbsWithBuilder';
import { CreateWorkTimeEntrySelectConstructionSitePageDefaultContent } from './CreateWorkTimeEntrySelectConstructionSitePageDefaultContent';
import { CreateWorkTimeEntrySelectConstructionSitePageSearchContent } from './CreateWorkTimeEntrySelectConstructionSitePageSearchContent';
import { RoutePaths } from '../../../classes/RoutePaths/RoutePaths';
import styles from './CreateWorkTimeEntrySelectConstructionSitePage.module.css';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

export type CreateWorkTimeEntrySelectConstructionSitePageProps =
  PropsWithoutChildren<Record<never, never>>;

export const CreateWorkTimeEntrySelectConstructionSitePage: FunctionComponent<CreateWorkTimeEntrySelectConstructionSitePageProps> =
  React.memo((props) => {
    prepareProps({ props, ignoreChildren: true });

    const { t } = useTranslation();
    const navigate = useNavigate();

    const workTimeEntryToCreate = useRequiredWorkTimeEntryToCreate();

    const { getRequiredCurrentUserPermissions } = useCurrentUser();

    useConfigureBreadcrumbsWithBuilder((builder) =>
      builder
        .addHomePage()
        .addCreateWorkTimeEntryPage()
        .addCreateWorkTimeEntrySelectConstructionSitePage()
    );

    const [searchText, setSearchText] = useState('');
    const {
      handleFocus: handleSearchInputFocus,
      handleBlur: handleSearchInputBlur,
      isFocused: searchInputFocused
    } = useIsFocused({});

    const constructionSites = useEntities(
      EntityName.ConstructionSite,
      'getByUserGroupId',
      workTimeEntryToCreate.userGroupId
    ).filter((cs) => !cs.archived);

    const showSearchContent = searchInputFocused || searchText;

    const handleCreateConstructionSiteClicked = useEventCallback(() => {
      navigate(RoutePaths.createWorkTimeEntryCreateConstructionSite());
    });

    const renderRightContentElements = useCallback(() => {
      const permissions = getRequiredCurrentUserPermissions();

      if (
        !permissions.canEditConstructionSitesOfUserGroup(
          workTimeEntryToCreate.userGroupId
        )
      )
        return [];

      return [
        <CreateEntityButton<null>
          key="HomePage.CreateWorkTimeEntryButton"
          text={t(
            'components.page.CreateWorkTimeEntrySelectConstructionSitePage.createConstructionSite'
          )}
          dropdownItems={[]}
          // should be fine here, since we only render this once
          // eslint-disable-next-line react/jsx-no-bind
          getDropdownItemDisplayText={() => ''}
          // eslint-disable-next-line react/jsx-no-bind
          getDropdownItemKey={() => ''}
          onCreateEntity={handleCreateConstructionSiteClicked}
          data-testid="CreateWorkTimeEntrySelectConstructionSitePage.CreateConstructionSiteButton"
        ></CreateEntityButton>
      ];
    }, [
      t,
      handleCreateConstructionSiteClicked,
      getRequiredCurrentUserPermissions,
      workTimeEntryToCreate.userGroupId
    ]);

    return (
      <PageContentContainer>
        <PageContentHeader
          title={t(
            'components.page.CreateWorkTimeEntrySelectConstructionSitePage.title'
          )}
          iconConfig={
            iconConfigurations.prefixed.page
              .CreateWorkTimeEntrySelectConstructionSitePage.titleIcon
          }
          renderRightContentElements={renderRightContentElements}
        ></PageContentHeader>

        <SearchInput
          placeholder={t(
            'components.page.CreateWorkTimeEntrySelectConstructionSitePage.searchInputPlaceholder'
          )}
          value={searchText}
          className={styles.searchInput}
          onInput={setSearchText}
          onFocus={handleSearchInputFocus}
          onBlur={handleSearchInputBlur}
          data-testid="CreateWorkTimeEntrySelectConstructionSitePage.SearchInput"
        ></SearchInput>

        {showSearchContent ? (
          <CreateWorkTimeEntrySelectConstructionSitePageSearchContent
            constructionSites={constructionSites}
            searchText={searchText}
          ></CreateWorkTimeEntrySelectConstructionSitePageSearchContent>
        ) : (
          <CreateWorkTimeEntrySelectConstructionSitePageDefaultContent
            constructionSites={constructionSites}
          ></CreateWorkTimeEntrySelectConstructionSitePageDefaultContent>
        )}
      </PageContentContainer>
    );
  });
